import { ReactNode, useMemo } from 'react';
import { createWalletClient, http } from 'viem';
import { publicProvider } from 'wagmi/providers/public';
import { walletConnectProvider, EIP6963Connector } from '@web3modal/wagmi';
import { InjectedConnector } from 'wagmi/connectors/injected';
import { WalletConnectConnector } from 'wagmi/connectors/walletConnect';
import { MockConnector } from 'wagmi/connectors/mock';
import { mnemonicToAccount } from 'viem/accounts';
import {
  Chain,
  configureChains,
  createConfig,
  WagmiConfig as WagmiConfigProvider,
} from 'wagmi';
import { sepolia, mainnet } from 'wagmi/chains';

import createSafeContext from '#/utils/createSafeContext';

import { useSystemConfig } from '#/features/system/system-config-context';

import * as TestWallet from '#tests/test-data/wallet';

/**
 * @see https://walletconnect.com/explorer
 */
const RECOMMENDED_WALLET_IDS = [
  'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
  '5864e2ced7c293ed18ac35e0db085c09ed567d67346ccb6f58a0327a75137489', // Fireblocks
];

interface WalletConnectConfig {
  readonly projectId: string;
  readonly wagmiConfig: ReturnType<typeof createConfig>;
  readonly recommendedWalletIds: string[];
  readonly chains: Chain[];
  readonly activeChain: Chain;
}

export const [useWalletConnectConfig, ConfigProvider] =
  createSafeContext<WalletConnectConfig>('WalletConnectConfig');

interface Props {
  readonly children: ReactNode;
}

const CHAIN_BY_ID: Record<number, Chain> = {
  1: mainnet,
  11155111: sepolia,
};

const availableChains = [mainnet, sepolia];

const projectId = WALLET_CONNECT_PROJECT_ID;

const { chains, publicClient } = configureChains(availableChains, [
  walletConnectProvider({ projectId }),
  publicProvider(),
]);

const connectors =
  IS_RUNNING_IN_CYPRESS === 'true'
    ? [
        new MockConnector({
          chains: [sepolia],
          options: {
            flags: {
              isAuthorized: true,
            },
            walletClient: createWalletClient({
              account: mnemonicToAccount(TestWallet.mnemonic),
              chain: sepolia,
              transport: http(),
            }),
          },
        }),
      ]
    : [
        new WalletConnectConnector({
          chains,
          options: {
            projectId,
            showQrModal: false,
            metadata: {
              name: 'Paradex Web3Modal',
              description: 'Paradex WebApp',
              url: FE_DOMAIN,
              icons: ['https://app.paradex.trade/favicon.png'],
            },
          },
        }),
        new EIP6963Connector({ chains }),
        new InjectedConnector({
          chains,
          options: {
            /**
             * Defaults to true, check back later if can be removed in the future versions of MetaMask
             * @see https://github.com/MetaMask/metamask-extension/issues/10353
             */
            shimDisconnect: true,
          },
        }),
      ];

const wagmiConfig = createConfig({
  autoConnect: true,
  connectors,
  publicClient,
});

export function WalletConnectConfigProvider(props: Props) {
  const config = useSystemConfig();
  const ethereumChainId = config.ethereum.chainId;

  const walletConnectConfig = useMemo((): WalletConnectConfig => {
    const recommendedWalletIds = RECOMMENDED_WALLET_IDS;
    const activeChain = CHAIN_BY_ID[ethereumChainId];

    if (activeChain == null) {
      throw new Error(
        `Chain with id ${ethereumChainId} is not supported.` +
          ` Supported chains are: ${Object.entries(CHAIN_BY_ID)
            .map(([id, chain]) => `${id} - ${chain.name}`)
            .join(', ')}}`,
      );
    }

    return {
      projectId,
      chains,
      activeChain,
      recommendedWalletIds,
      wagmiConfig: wagmiConfig as ReturnType<typeof createConfig>,
    };
  }, [ethereumChainId]);

  return (
    <WagmiConfigProvider config={walletConnectConfig.wagmiConfig}>
      <ConfigProvider value={walletConnectConfig}>
        {props.children}
      </ConfigProvider>
    </WagmiConfigProvider>
  );
}
